import * as angular from 'angular';
import { Growl } from 'angular/custom/services/growl.service';
import { ErrorHandler } from 'app/_services/error-handler.factory';

class ProgressiveInput implements ng.IDirective {
    public restrict = 'A';
    public scope = false;
    public require = 'ngModel';

    constructor(
        private growl: Growl,
        private errorHandler: ErrorHandler
    ) {}

    public link: ng.IDirectiveLinkFn = (
        scope: ng.IScope,
        elem: JQuery,
        attrs: ng.IAttributes,
        ngModelCtrl: ng.INgModelController
    ) => {
        /**
         * Sometimes (observed this especially in Chrome), Ctrl+Enter causes the blur event on the element leading to double
         * form submission, thus we need to take care of that state.
         */
        let currentlySubmittingViaCtrlEnter: boolean;
        let hasAlreadyHadFocusOnce: boolean = false;
        let lastSavedViewValue: any;
        let clickOnUndoCausingBlur: boolean;
        let clickOnCheckCausingBlur: boolean;
        let textAreaHasAutoGrowHandlerAttached: boolean = false;

        const tagNameLowerCase = elem[0].tagName.toLowerCase();

        const inputViewChangeListener: () => void = (): void => {
            if (tagNameLowerCase === 'input') {
                elem.attr('size', 1 + ngModelCtrl.$viewValue.length);
            }
        };

        const progressiveInput: JQuery = elem
            .wrap('<div class="progressive-input"></div>')
            .parent();
        const pencil: JQuery = angular.element(
            '<i class="fa fa-pencil" title="Edit" aria-hidden="true"></i>'
        );
        const check: JQuery = angular.element(
            '<i class="fa fa-check" title="Save (Ctrl+Enter)" aria-hidden="true"></i>'
        );
        const undo: JQuery = angular.element(
            '<i class="fa fa-undo" title="Undo (Esc)" aria-hidden="true"></i>'
        );
        const loading: JQuery = angular.element(
            `<div id="loading-bar-spinner-inline"><div class="spinner-icon-inline"></div></div>`
        );

        elem.before(pencil);
        elem.before(check);
        elem.before(undo);
        elem.before(loading);

        if (
            tagNameLowerCase === 'input' &&
            !elem.parents('.input-group').length
        ) {
            progressiveInput.addClass('inline-block');
        }

        ngModelCtrl.$render = function () {
            elem.val(ngModelCtrl.$viewValue);
            lastSavedViewValue = ngModelCtrl.$viewValue;
            inputViewChangeListener();
        };

        elem.keyup(() => {
            inputViewChangeListener();

            if (angular.equals(lastSavedViewValue, ngModelCtrl.$viewValue)) {
                check.addClass('disabled');
                undo.addClass('disabled');
            } else {
                check.removeClass('disabled');
                undo.removeClass('disabled');
            }

            if (ngModelCtrl.$invalid) {
                check.addClass('disabled');
            }
        });

        progressiveInput.mouseenter(() => {
            if (!elem.is(':focus') && !elem.prop('disabled')) {
                pencil.fadeIn();
            }
        });

        progressiveInput.mouseleave(() => {
            pencil.fadeOut();
        });

        pencil.click(() => {
            elem.focus();
        });

        elem.focus(() => {
            if (!textAreaHasAutoGrowHandlerAttached) {
                // autogrow is an addon for jquery, we have no @types definition for
                // @ts-ignore
                elem.autogrow({ vertical: true, horizontal: false });
                textAreaHasAutoGrowHandlerAttached = true;
            }

            if (!hasAlreadyHadFocusOnce) {
                hasAlreadyHadFocusOnce = true;
                lastSavedViewValue = ngModelCtrl.$viewValue;
            }

            if (angular.equals(lastSavedViewValue, ngModelCtrl.$viewValue)) {
                check.addClass('disabled');
                undo.addClass('disabled');
            } else {
                check.removeClass('disabled');
                undo.removeClass('disabled');
            }

            if (ngModelCtrl.$invalid) {
                check.addClass('disabled');
            }

            check.fadeIn();
            undo.fadeIn();
        });

        if (this.isFocusOnCreationEnabled(scope, attrs)) {
            elem.focus();
        }

        elem.blur((e: JQueryEventObject) => {
            if (!clickOnUndoCausingBlur && !currentlySubmittingViaCtrlEnter) {
                submit();
                return;
            }

            clickOnUndoCausingBlur = false;
            elem.focus();
        });

        undo.mousedown(() => {
            ngModelCtrl.$setViewValue(lastSavedViewValue);
            ngModelCtrl.$render();
            clickOnUndoCausingBlur = true;
            check.addClass('disabled');
            undo.addClass('disabled');
            ProgressiveInput.onUndo(scope, attrs);
        });

        check.mousedown(() => {
            clickOnCheckCausingBlur = true;
        });

        elem.keydown((e: JQueryKeyEventObject) => {
            if (angular.equals(ngModelCtrl.$viewValue, lastSavedViewValue)) {
                return;
            }

            if (e.ctrlKey && e.keyCode === 13 && ngModelCtrl.$valid) {
                currentlySubmittingViaCtrlEnter = true;
                submit(() => (currentlySubmittingViaCtrlEnter = false));
            } else if (e.keyCode === 27) {
                ngModelCtrl.$setViewValue(lastSavedViewValue);
                ngModelCtrl.$render();
            }
        });

        const submit: (SubmitFnCallbackType?) => void = (
            cb?: (cb?: boolean) => void
        ) => {
            const valueToBeSaved: any = ngModelCtrl.$viewValue;

            if (
                angular.equals(valueToBeSaved, lastSavedViewValue) ||
                ngModelCtrl.$invalid
            ) {
                check.fadeOut(0);
                undo.fadeOut(0);
                cb && cb(false);
                if (angular.equals(valueToBeSaved, lastSavedViewValue)) {
                    ProgressiveInput.onNoChangeSubmit(scope, attrs);
                }
                return;
            }

            const promise: ng.IPromise<any> = scope.$eval(
                attrs['pkpProgressiveInput']
            );

            toggleLoading();

            promise &&
                promise
                    .then(() => {
                        this.growl.show('Successfully saved.', 'success', {
                            delay: 2000
                        });
                        lastSavedViewValue = valueToBeSaved;
                    })
                    .catch(this.errorHandler)
                    .finally(() => {
                        toggleLoading();
                        check.fadeOut(0);
                        undo.fadeOut(0);
                        pencil.fadeOut(0);
                        cb && cb(true);
                    });
        };

        const toggleLoading: () => void = () => {
            loading.toggle();
            progressiveInput.toggleClass('loading');
            elem.prop('disabled', !elem.prop('disabled'));
        };
    };

    public isFocusOnCreationEnabled(
        scope: ng.IScope,
        attrs: ng.IAttributes
    ): boolean {
        return attrs['pkpProgressiveInputFocusOnCreation'] != null;
    }

    private static onNoChangeSubmit(scope: ng.IScope, attrs: ng.IAttributes) {
        scope.$eval(attrs['pkpProgressiveInputOnNoChangeSubmit']);
    }

    private static onUndo(scope: ng.IScope, attrs: ng.IAttributes) {
        scope.$eval(attrs['pkpProgressiveInputOnUndo']);
    }
}

ProgressiveInput.$inject = ['growl'];

angular
    .module('app')
    .directive(
        'pkpProgressiveInput',
        (growl, errorHandler) => new ProgressiveInput(growl, errorHandler)
    );
